import { message } from "antd";
import { type ReactNode, useContext } from "react";
import { type RouteComponentProps } from "react-router";

import { IconClientUpdates, IconListChecks, IconSparkle, IconStamp } from "@btnet/blueprint-icons";
import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";

import {
    type AppDefaultInfo,
    type BuilderInfo,
    type EnvironmentInfo,
    type UserInfo,
} from "helpers/AppProvider.types";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes, MenuItemName, OpenActivityType } from "types/enum";

import { type ITrackingProp } from "utilities/analytics/analytics";
import { getEditModeLink } from "utilities/editMode/edit.utility";
import { externalPages } from "utilities/externalPages";
import { billAddRoute } from "utilities/flatRoutes/bill.routes";
import { isInJobRoute, jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { preliminaryEstimateRoute } from "utilities/flatRoutes/preliminaryEstimate.routes";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { warrantyAddRoute } from "utilities/flatRoutes/warranty.routes";
import { routes } from "utilities/routes";
import { showChat as showServiceCloudChat } from "utilities/thirdPartyIntegrations/serviceCloud/serviceCloud";

import {
    BTIconAdditionalServices,
    BTIconArrowsClockwise,
    BTIconBank,
    BTIconBidPackages,
    BTIconBillsPurchaseOrders,
    BTIconBudget,
    BTIconBulb,
    BTIconChangeOrders,
    BTIconClockCounterClockwise,
    BTIconComments,
    BTIconCommunities,
    BTIconContacts,
    BTIconCopy,
    BTIconDailyLogs,
    BTIconDocuments,
    BTIconEstimate,
    BTIconGearSix,
    BTIconHelpCenter,
    BTIconInfo,
    BTIconIntegrations,
    BTIconInternalUsers,
    BTIconJobs,
    BTIconLeadProposals,
    BTIconLeads,
    BTIconLearningAcademy,
    BTIconListBullets,
    BTIconLiveChat,
    BTIconMapPin,
    BTIconMarketplace,
    BTIconMessages,
    BTIconOwnerInvoices,
    BTIconPhoneOutline,
    BTIconPhotos,
    BTIconPlusCircleTransparent,
    BTIconPriceTag,
    BTIconPurchaseOrders,
    BTIconRebates,
    BTIconReceipts,
    BTIconRFIs,
    BTIconSchedules,
    BTIconSelections,
    BTIconSettingsBlack,
    BTIconSignOut,
    BTIconSpecifications,
    BTIconSubs,
    BTIconSubscription,
    BTIconSurveys,
    BTIconTable,
    BTIconTimeClock,
    BTIconTodos,
    BTIconVideos,
    BTIconWarranties,
} from "commonComponents/btWrappers/BTIcon";
import { BTUser } from "commonComponents/btWrappers/BTUser/BTUser";
import { type IMainNavChildNodeWithSortingData } from "commonComponents/utilities/MainNavigation/MainNavigation";
import { type IMainNavigationHandler } from "commonComponents/utilities/MainNavigation/MainNavigation.api.handler";
import {
    type HeaderStyleInfo,
    type IRiskInsurance,
    type MainNavigationResponse,
    type NavHeaderResponse,
    type NavMenuItemResponse,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.types";

import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { myUserDetailsRoute } from "entity/internalUser/InternalUserDetails/myUserDetails.routes";
import { JobListTabs } from "entity/job/JobList/JobList.api.types";
import { LeadTabTypes } from "entity/lead/Common/LeadTab/LeadTab.types";
import { handleLogout } from "entity/login/Login/Login.utils";
import { PurchasingDashboardPages } from "entity/rebate/PurchasingDashboard/PurchasingDashboard.types";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";
import { SelectionListContainerTabs } from "entity/selection/SelectionListContainer/SelectionListContainer.types";
import { hasNewClientSelectionOptionsExperience } from "entity/selectionOption/selectionOptionsUtilities";

/**
 * Constants
 */
export const MAIN_NAVIGATION_BREAKPOINT = 1150;
export const MAIN_NAVIGATION_TOP_LEVEL_MENUS: TMainNavMenuKeys[] = [
    "sales",
    "job",
    "projectManagement",
    "files",
    "messaging",
    "financial",
    "reports",
];
export const MAIN_NAVIGATION_TOP_LEVEL_ACTIONS: TMainNavMenuKeys[] = ["users", "help", "setup"];
export const FlatMenus: (keyof MenuInfo)[] = ["reports", "notifications"];
export const CustomMenus: (keyof MenuInfo)[] = ["notifications"];
export const menuItemClass = "MainNavMenuItem";
export const quickAddClass = "MainNavMenuItemQuickAdd MenuLink";
const isSinglePageApp = window.location.pathname.toLowerCase().startsWith("/app");

export enum MenuOnClickTypes {
    Anchor = 1,
    /** Link relative to the current page */
    BTLinkRelative = 2,
    External = 3,
    NoLink = 4,
    /** Absolute link, if you want to add to the current route use BTLinkRelative */
    BTLink = 5,
}

/**
 * Core Type Definitions
 */

/**
 * The current template's Selections experience, resolved asynchronously from its job-level config.
 * The nav renders before this settles, so the flag is tri-state: items gated on it can require a
 * definite answer and stay hidden until one arrives rather than flashing the wrong state.
 */
export interface ITemplateSelectionsState {
    /**
     * Whether the current template is a Selections Reimagined template, or undefined when that is
     * not known — still loading, nothing fetched for this template, or the config failed to load.
     */
    isSelectionsReimaginedTemplate: boolean | undefined;
}

// Menu Configuration Types
export interface IMenuTabInfo {
    testid: string;
    /**
     * The MenuItemName(s) a page reports via its route's `selectedMenuItem` prop. Used to highlight
     * the active nav item on any page load (deep links, redirects, refresh) rather than relying on
     * antd's uncontrolled last-click behavior. Keeping this on the nav item definition avoids a
     * separate enum-to-key lookup map that would rot as nav items change. Provide an array when more
     * than one page maps to the same nav item (e.g. owner invoices and progress invoices).
     */
    menuItemName?: MenuItemName | MenuItemName[];
    onClickType?: (
        builderInfo?: BuilderInfo,
        userInfo?: UserInfo,
        info?: IMenuTabInfoLinkUrlInfo,
    ) => MenuOnClickTypes;
    icon?: React.ReactNode;
    onClick?: (
        request: IMenuItemClickRequest,
        data: MainNavigationResponse,
        routeProps: RouteComponentProps,
        appDefaultInfo: AppDefaultInfo | undefined,
        userInfo: UserInfo | null | undefined,
    ) => void;
    getLinkUrl?: (info: IMenuTabInfoLinkUrlInfo) => string | IRouteLocationState;
    quickAddInfo?: IMenuTabInfoQuickAdd[];
    showInTemplateMode?: (
        builderInfo?: BuilderInfo | null,
        templateSelectionsState?: ITemplateSelectionsState,
    ) => boolean;
    getTitle?: (baseTitle: string, isInTemplateMode: boolean) => string;
    enableForSingleJobOnly?: (builderInfo?: BuilderInfo | null) => boolean;
    showOnPortal?: BTLoginTypes[];

    /**
     * Use this property to include a tag after the menu item text
     * @see StatusTagDisplay for tag examples/types
     */
    tag?: React.ReactNode;
    getIsDisabled?: (userInfo?: UserInfo, builderInfo?: BuilderInfo) => boolean | undefined;

    /**
     * When true, render a divider immediately above this item in its dropdown,
     * visually separating it from the items before it.
     */
    dividerBefore?: boolean;
}

/**
 * A typeguard used for iterating over MenuInfo in a typesafe manner and differentiating fields such as testid from fields with constant menu data.
 */
export function isIMenuTabInfo(value: IMenuTabInfo | string): value is IMenuTabInfo {
    return typeof value !== "string";
}

export interface IMenuTabInfoLinkUrlInfo {
    loginType: BTLoginTypes;
    data: MainNavigationResponse;
    jobId: number;
    isTemplateMode: boolean;
    builderInfo?: BuilderInfo;
    userInfo?: UserInfo | null;
    appDefaultInfo?: AppDefaultInfo;
    environmentInfo?: EnvironmentInfo;
}

export interface IQuickAddReactRoute {
    pathname: string;
    state: {
        edit: boolean;
    };
}

export interface IMenuTabInfoQuickAdd {
    entity: string;
    useLabel?: boolean;

    quickAddReactRoute: (request: IMenuItemClickRequest) => string | IQuickAddReactRoute;
    canQuickAdd?: (data: MainNavigationResponse) => boolean;
    onClick?: () => void;
}

// Click/Navigation Types
export interface IMenuItemClickRequest {
    userId: number;
    builderId: number;
    jobId: number;
    isTemplateMode: boolean;
    builderInfo?: BuilderInfo | null;
}

// Component Props Types
export interface IMainNavigationProps extends ITrackingProp {
    className?: string;
    handler?: IMainNavigationHandler;
    menuInfo?: MenuInfo;
    builderId: number;
    userId: number;
    jobId: number;
    generalJobId?: number;
    selectedMenuItem?: MenuItemName;
    isTemplateMode: boolean;
    loginType: BTLoginTypes;
    riskInsurance?: IRiskInsurance;
    noBreakpoints?: boolean;
}

export interface IMenuItemsProps extends IMainNavigationProps {
    setMainNavigationRef: (node: HTMLDivElement) => void;
    headerStyleInfo: HeaderStyleInfo;
    isSmallWidth: boolean;
    data: MainNavigationResponse;
}
export interface IMainNavMenusProps {
    setMainNavigationRef: (node: HTMLDivElement) => void;
    headerStyleInfo: HeaderStyleInfo;
    isSmallWidth: boolean;
    loginType: BTLoginTypes;
    /** data for collapseable left nav */
    navNodes: IMainNavNode[];
    /** right nav items that should be rendered as Menu.SubMenu -- e.g. help, users, settings */
    actionNavNodes: IMainNavNode[];
    quickaddNode: IMainNavNode<IMainNavChildNodeWithSortingData>;
    handleShowDrawer: () => void;
    handleCloseDrawer: () => void;
    menuOverflowOpen: boolean;
    noBreakpoints?: boolean;
    className?: string;
    onNodeActivate: (
        info: IMenuTabInfo | undefined,
        navItem: NavMenuItemResponse | undefined,
        title: string,
    ) => void;
    openKeys: string[];
    onOpenChange: (keys: string[]) => void;
    data: MainNavigationResponse;
    jobId: number;
    isTemplateMode: boolean;
    builderInfo?: BuilderInfo;
    userInfo: UserInfo;
    appDefaultInfo?: AppDefaultInfo;
    environmentInfo?: EnvironmentInfo;
    selectedMenuItem?: MenuItemName;
    subMenuProps: Omit<IBTSubMenuItemProps, "itemKey">;
    handler: IMainNavigationHandler;
    setOpenKeys: (keys: string[]) => void;
}
export interface IMainNavTitleProps {
    readonly title: string | ReactNode;
    readonly showNavCaret: boolean;
    readonly testid: string;
    readonly closed: boolean;
    readonly ariaLabel?: string;
}
export interface IMainNavVerticalLocalProps {
    readonly children: React.ReactNode;
    readonly onOpen?: () => void;
    loginType: BTLoginTypes;
    backgroundColor: string;
}

export interface IMainNavHomeButtonProps {
    readonly loginType: BTLoginTypes;
    inDrawer?: boolean;
    backgroundColor: string;
}

export interface IBTSubMenuItemProps {
    itemKey: keyof MenuInfo;

    /** menu icon to display */
    icon?: ReactNode;

    /**
     * should only the icon be shown
     * @default false
     */
    iconOnly?: boolean;

    className?: string;
    countForBadge?: number;

    menuInfo: MenuInfo;

    selectedMenuItem: MenuItemName;
    jobId: number;
    isTemplateMode: boolean;
    loginType: BTLoginTypes;

    data: MainNavigationResponse;

    routeInfo: IMenuItemClickRequest;

    mainNavResponse: MainNavigationResponse;

    builderInfo?: BuilderInfo;

    onContentClick: (menuTabInfo: IMenuTabInfo, navMenuItem: NavMenuItemResponse) => void;

    onCustomMenuClick?: () => void;

    children?: React.ReactNode;

    userInfo: UserInfo;
    appDefaultInfo?: AppDefaultInfo;
    envInfo?: EnvironmentInfo;
    onItemClick?: (key: string, isExternalLink?: boolean) => void;
    destroyPopupOnHide?: boolean;

    /**
     * Resolved once by the nav root and passed down so each dropdown does not refetch the same
     * per-template config. Optional because only the Selections item's showInTemplateMode reads it;
     * dropdowns that never render that item can omit it.
     */
    templateSelectionsState?: ITemplateSelectionsState;
    "data-testid"?: string;
    "aria-label"?: string;
}

// Navigation Tree Types
export interface IMainNavNode<T = IMainNavChildNode> extends IMainNavChildNode {
    children?: T[];
}

export interface IMainNavChildNode {
    key: string;
    title: string;
    testId: string;
    icon?: React.ReactNode;
    info: IMenuTabInfo;
    navItem: NavMenuItemResponse;
    disabled: boolean;
    /** MenuItemName(s) this nav item represents, used to highlight the active item. @see IMenuTabInfo.menuItemName */
    menuItemName?: MenuItemName | MenuItemName[];
}

// Notification Types
export interface INotificationUnfollowModel {
    builderId: number;
    notificationId: number;
}

// Menu Key Types
export type TMainNavMenuKeys = keyof MenuInfo & keyof MainNavigationResponse;

/**
 * Utility Functions
 */

// Internal utilities
const refreshOnClose = () => {
    window.location.reload();
};

const getTemplateModeTitleDefault = (inputTitle: string, isTemplateMode: boolean) => {
    return isTemplateMode ? inputTitle.replace("Job", "Template") : inputTitle;
};

const showChat = (appDefaultInfo: AppDefaultInfo | undefined) => {
    if (appDefaultInfo?.enableServiceCloudChat) {
        showServiceCloudChat();
    }
};

/**
 * Determine if we get the route basename (appBase) for free with usage of BTLink in the SPA page
 * If not we need to add it manually.
 * @param route
 * @returns
 */
function getSPACheckedRoute(route: string) {
    return isSinglePageApp ? route : `${routes.appBase}${route}`;
}

// Public helpers
export const getLearningAcademyUrl = (appDefaultInfo: AppDefaultInfo | undefined) => {
    return appDefaultInfo?.buildertrendLearningAcademyURL;
};

export function getCommunityUrl(
    appDefaultInfo: AppDefaultInfo | undefined,
    isImpersonatingBuilder: boolean,
) {
    return !isImpersonatingBuilder ? appDefaultInfo?.communityURL : "";
}
export const getProjectManagementUrl = (appDefaultInfo: AppDefaultInfo | undefined) => {
    return `${appDefaultInfo?.buildertrendLearningAcademyURL}${appDefaultInfo?.learningAcademyConfig.projectManagement}`;
};

export const getTakeoffUrl = (appDefaultInfo: AppDefaultInfo | undefined) => {
    return `${appDefaultInfo?.buildertrendLearningAcademyURL}${appDefaultInfo?.learningAcademyConfig.takeOff}`;
};

export const getCostCodesBestPracticeUrl = (appDefaultInfo: AppDefaultInfo | undefined) => {
    return `${appDefaultInfo?.buildertrendLearningAcademyURL}${appDefaultInfo?.learningAcademyConfig.costCodes}`;
};

// Navigation utilities
export function getQuickAddReactRoute(route: string) {
    return "/QuickAction" + route;
}

// Quick add menu generation
// Base list (always included)
const BASE_QUICKADD_MENU_OPTIONS: IOptionData[] = [
    { parentKey: "sales", itemKey: "leadOpportunities", title: "Lead Opportunity" },
    { parentKey: "sales", itemKey: "leadActivities", title: "Lead Activity" },
    { parentKey: "sales", itemKey: "leadProposals", title: "Lead Proposal" },
    { parentKey: "projectManagement", itemKey: "schedule", title: "Schedule Item" },
    { parentKey: "projectManagement", itemKey: "dailyLogs", title: "Daily Log" },
    { parentKey: "projectManagement", itemKey: "todos", title: "To-Do" },
    { parentKey: "projectManagement", itemKey: "changeOrders", title: "Change Order" },
    { parentKey: "projectManagement", itemKey: "warranty", title: "Warranty Claim" },
    { parentKey: "projectManagement", itemKey: "timeClock", title: "Time Clock Shift" },
    { parentKey: "projectManagement", itemKey: "specifications", title: "Specifications" },
    { parentKey: "messaging", itemKey: "messages", title: "Message" },
    { parentKey: "messaging", itemKey: "rfis", title: "RFI" },
    { parentKey: "financial", itemKey: "bids", title: "Bid Package" },
    { parentKey: "financial", itemKey: "purchaseOrders", title: "Purchase Order" },
    { parentKey: "financial", itemKey: "bills", title: "Bill" },
    { parentKey: "financial", itemKey: "receipts", title: "Receipt" },
    { parentKey: "financial", itemKey: "ownerInvoices", title: "Invoice" },
    { parentKey: "users", itemKey: "internalUsers", title: "Internal User" },
    { parentKey: "users", itemKey: "subs", title: "Sub/Vendor" },
    { parentKey: "users", itemKey: "contacts", title: "Client Contact" },
];

interface IOptionData {
    parentKey: keyof MenuInfo;
    itemKey: string;
    title: string;
}

export const getQuickAddMenuOptions = (
    isFullySelectionsReimagined: boolean,
    isTemplateMode: boolean,
    isLegacySelectionsBuilder: boolean,
): IOptionData[] => {
    const items = [...BASE_QUICKADD_MENU_OPTIONS];
    // can be removed once template work is completed
    // hides quick add selections in templates for all non legacy builders
    if (isLegacySelectionsBuilder || !isTemplateMode) {
        const selectionsItem: IOptionData = {
            parentKey: "projectManagement",
            itemKey: "selections",
            title: isFullySelectionsReimagined ? "Selection Option" : "Selection",
        };
        items.push(selectionsItem);
    }
    return items;
};

/**
 * Menu Class Definitions
 */
class SalesMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "sales-main-nav-button";
    leadOpportunities: IMenuTabInfo = {
        testid: "leadOpportunities",
        menuItemName: MenuItemName.LeadOpportunities,
        icon: <BTIconLeads size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.lead.getLeadListLink(LeadTabTypes.ListView));
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Lead Opportunity",
                quickAddReactRoute: () => getQuickAddReactRoute(routes.lead.getDetailsLink(0)),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    leadActivities: IMenuTabInfo = {
        testid: "leadActivities",
        menuItemName: MenuItemName.LeadActivities,
        icon: <BTIconPhoneOutline size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.lead.getLeadListLink(LeadTabTypes.ActivityView));
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Lead Activity",
                quickAddReactRoute: () =>
                    getEditModeLink(
                        getQuickAddReactRoute(
                            routes.leadActivity.getDetailsLink(
                                0,
                                0,
                                OpenActivityType.Edit,
                                undefined,
                            ),
                        ),
                    ),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    leadProposals: IMenuTabInfo = {
        testid: "leadProposals",
        menuItemName: MenuItemName.LeadProposals,
        icon: <BTIconLeadProposals size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.lead.getLeadListLink(LeadTabTypes.Proposals));
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Lead Proposal",
                quickAddReactRoute: () =>
                    getQuickAddReactRoute(`${routes.lead.getDetailsLink(-1)}/LeadProposal/${0}`),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    activityCalendar: IMenuTabInfo = {
        testid: "activityCalendar",
        menuItemName: MenuItemName.LeadActivityCalendar,
        icon: <BTIconSchedules size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.lead.getLeadListLink(LeadTabTypes.ActivityCalendar));
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Lead Activity",
                quickAddReactRoute: () =>
                    getQuickAddReactRoute(
                        routes.leadActivity.getDetailsLink(0, 0, OpenActivityType.Edit, undefined),
                    ),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    leadMap: IMenuTabInfo = {
        testid: "leadMap",
        menuItemName: MenuItemName.LeadMap,
        icon: <BTIconMapPin size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.lead.getLeadListLink(LeadTabTypes.Map));
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getTitle: getTemplateModeTitleDefault,
    };
    preliminaryEstimate: IMenuTabInfo = {
        testid: "preliminaryEstimate",
        icon: (
            <div className="flex justify-content-center" style={{ width: "24px" }}>
                <IconSparkle />
            </div>
        ),
        getLinkUrl: () => preliminaryEstimateRoute.link(),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        showInTemplateMode: () => false,
        dividerBefore: true,
        tag: (
            <BTStatusBadge
                testid="preliminaryEstimateBetaTag"
                textOnly
                text="BETA"
                displayType="info"
            />
        ),
        getTitle: getTemplateModeTitleDefault,
    };
}

class JobMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "job-main-nav-button";
    summary: IMenuTabInfo = {
        testid: "summary",
        menuItemName: MenuItemName.Summary,
        icon: <BTIconJobs size="large" />,
        onClickType: (
            _builderInfo: BuilderInfo | undefined,
            userInfo: UserInfo | undefined,
            _info?: IMenuTabInfoLinkUrlInfo,
        ) => {
            if (
                userInfo?.loginType === BTLoginTypes.OWNER ||
                userInfo?.loginType === BTLoginTypes.CONTACT ||
                userInfo?.loginType === BTLoginTypes.BUILDER ||
                userInfo?.loginType === BTLoginTypes.SUBS
            ) {
                return isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor;
            }
            return MenuOnClickTypes.Anchor;
        },
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (info.loginType === BTLoginTypes.OWNER || info.loginType === BTLoginTypes.CONTACT) {
                return getSPACheckedRoute(routes.ownerSummary.getLink());
            } else if (info.loginType === BTLoginTypes.BUILDER) {
                return getSPACheckedRoute(routes.landingPage.getLink());
            }

            return getSPACheckedRoute(routes.subSummary.getLink());
        },
        getTitle: getTemplateModeTitleDefault,
    };
    jobInfo: IMenuTabInfo = {
        testid: "jobInfo",
        menuItemName: MenuItemName.JobInfo,
        icon: <BTIconInfo size="large" />,
        onClickType: (_builderInfo, _userInfo, info) => {
            if (!info?.isTemplateMode) {
                return MenuOnClickTypes.BTLink;
            } else {
                return MenuOnClickTypes.BTLinkRelative;
            }
        },
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (!info.isTemplateMode) {
                return jobDetailRoute.link({ jobId: info.jobId });
            } else {
                return "/QuickAction" + routes.template.getTemplateDetailsLink(info.jobId);
            }
        },
        showInTemplateMode: () => true,
        enableForSingleJobOnly: () => true,
        getTitle: getTemplateModeTitleDefault,
        getIsDisabled: (_userInfo) => {
            return isInJobRoute(window.location.pathname);
        },
    };
    subJobInfo: IMenuTabInfo = {
        testid: "subJobInfo",
        icon: <BTIconInfo size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.job.getListLink()),
        showOnPortal: [BTLoginTypes.SUBS],
        getTitle: getTemplateModeTitleDefault,
    };
    jobPriceSummary: IMenuTabInfo = {
        testid: "jobPriceSummary",
        menuItemName: MenuItemName.JobPriceSummary,
        onClickType: () => MenuOnClickTypes.BTLink,
        icon: <BTIconPriceTag size="large" />,
        getLinkUrl: () => {
            return `${routes.appBase}${routes.job.getJobFinancialsLink()}`;
        },
        getTitle: getTemplateModeTitleDefault,
    };
    jobsList: IMenuTabInfo = {
        testid: "jobsList",
        menuItemName: MenuItemName.JobsList,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconListBullets size="large" />,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (info.isTemplateMode) {
                return getSPACheckedRoute(routes.template.getListLink());
            } else {
                return getSPACheckedRoute(routes.job.getListLink());
            }
        },

        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    jobsMap: IMenuTabInfo = {
        testid: "jobsMap",
        menuItemName: MenuItemName.JobsMap,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconMapPin size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.job.getListLink(JobListTabs.Map));
        },
        getTitle: getTemplateModeTitleDefault,
    };
    newJobFromScratch: IMenuTabInfo = {
        testid: "newJobFromScratch",
        menuItemName: MenuItemName.NewJobFromScratch,
        showInTemplateMode: () => true,
        onClickType: (_builderInfo, _userInfo, info) => {
            if (!info?.isTemplateMode) {
                return MenuOnClickTypes.BTLink;
            } else {
                return MenuOnClickTypes.BTLinkRelative;
            }
        },
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (!info.isTemplateMode) {
                return jobDetailRoute.link({ jobId: 0 }, { openCondensed: true });
            } else {
                return "/QuickAction" + routes.template.getTemplateDetailsLink(0);
            }
        },
        icon: <BTIconPlusCircleTransparent size="large" />,
        getTitle: getTemplateModeTitleDefault,
        getIsDisabled: (_userInfo) => {
            return isInJobRoute(window.location.pathname);
        },
    };
    newJobFromTemplate: IMenuTabInfo = {
        testid: "newJobFromTemplate",
        menuItemName: MenuItemName.NewJobFromTemplate,
        showInTemplateMode: () => true,
        icon: <BTIconCopy size="large" />,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) =>
            "/QuickAction" +
            (info.isTemplateMode
                ? routes.template.getTemplateFromTemplateLink()
                : routes.job.getJobFromTemplateLink(0, 0, "0")),
        getTitle: getTemplateModeTitleDefault,
        getIsDisabled: (_userInfo) => {
            return isInJobRoute(window.location.pathname);
        },
    };
}

class ProjectManagementMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "project-management-main-nav-button";
    schedule: IMenuTabInfo = {
        testid: "schedule",
        menuItemName: MenuItemName.Calendar,
        icon: <BTIconSchedules size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(
                routes.schedule.getListLink(ScheduleListContainerTabs.Default),
            );
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Schedule Item",
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.schedule.getDetailsLink(0, request.jobId)),
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    dailyLogs: IMenuTabInfo = {
        testid: "dailyLogs",
        menuItemName: MenuItemName.DailyLogs,
        icon: <BTIconDailyLogs size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.dailyLog.getListLink()),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Daily Log",
                quickAddReactRoute: (request) => routes.dailyLog.getAddPageLink(request.jobId),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    todos: IMenuTabInfo = {
        testid: "todos",
        menuItemName: MenuItemName.ToDos,
        icon: <BTIconTodos size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.toDo.getListLink()),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "To-Do",
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(
                        routes.toDo.getDetailsLink(0, request.jobId, false, false),
                    ),
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    tasks: IMenuTabInfo = {
        testid: "tasks",
        icon: (
            <div className="flex justify-content-center" style={{ width: "24px" }}>
                <IconListChecks />
            </div>
        ),
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.task.getListLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
        tag: <BTStatusBadge testid="taskNewTag" textOnly text="New" displayType="info" />,
        showOnPortal: [
            BTLoginTypes.BUILDER,
            BTLoginTypes.OWNER,
            BTLoginTypes.CONTACT,
            BTLoginTypes.SUBS,
        ],
    };
    changeOrders: IMenuTabInfo = {
        testid: "changeOrders",
        menuItemName: MenuItemName.ChangeOrders,
        icon: <BTIconChangeOrders size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.changeOrder.getListLink()),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Change Order",
                quickAddReactRoute: (request) => {
                    return getQuickAddReactRoute("/ChangeOrderDraft/" + request.jobId);
                },
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    selections: IMenuTabInfo = {
        testid: "selections",
        menuItemName: MenuItemName.Selections,
        icon: <BTIconSelections size="large" />,
        onClickType: () => {
            return isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor;
        },
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            const isSelectionsReimagined = info.builderInfo?.flags.selectionsReimagined ?? false;

            // The new selections page will appear in the owner portal if the SelectionsReimagined flag is on
            const navigateToNewSelectionsPage = hasNewClientSelectionOptionsExperience(
                isSelectionsReimagined,
                info.loginType,
            );

            const isBuilder = info.loginType === BTLoginTypes.BUILDER;
            if (isBuilder) {
                return getSPACheckedRoute(
                    routes.selection.getListLink(SelectionListContainerTabs.Default),
                );
            }
            if (navigateToNewSelectionsPage) {
                return getSPACheckedRoute(routes.selectionOptions.ownerList);
            }

            const isSub = info.loginType === BTLoginTypes.SUBS;
            if (isSub) {
                return getSPACheckedRoute(routes.selection.getSubSelectionsListLink());
            }

            return getSPACheckedRoute(
                routes.selection.getListLink(SelectionListContainerTabs.Default),
            );
        },
        quickAddInfo: [
            {
                entity: "Selection",
                // Routes to a dispatcher rather than deciding here: on a mixed builder the legacy
                // vs Selections Reimagined create flow depends on the *job*, which isn't known
                // until one is selected (jobId is 0 when it isn't).
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.selection.getQuickAddLink(request.jobId)),
            },
        ],
        // Hiding legacy Selections on SRI templates is temporary: it goes away once Selections is
        // re-enabled for templates, at which point this collapses back to a builder-level check.
        //
        // The SRI flag is a master kill switch (mirrors BuilderIsFullySelectionsReimagined), so
        // check it first: with it off, show the item unconditionally and never wait on per-template
        // config that could only ever answer "not SRI".
        //
        // Template Support for Selection Options starts that collapse: with it on, Selections is
        // reachable on any template, so skip the per-template gating.
        //
        // With SRI on and that flag off, require a definite "not SRI" for this template. Anything
        // less stays hidden: showing the item for a template that turns out to be SRI is the bug
        // this gating prevents.
        showInTemplateMode: (builderInfo, templateSelectionsState) => {
            if (!builderInfo?.flags.selectionsReimagined) {
                return true;
            }
            if (builderInfo.flags.templateSupportForSelectionOptions) {
                return true;
            }
            if (!builderInfo.hasLegacySelectionsData) {
                return false;
            }
            return templateSelectionsState?.isSelectionsReimaginedTemplate === false;
        },
        getTitle: getTemplateModeTitleDefault,
    };
    warranty: IMenuTabInfo = {
        testid: "warranty",
        menuItemName: MenuItemName.Warranty,
        icon: <BTIconWarranties size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.warranty.getListLink()),
        quickAddInfo: [
            {
                entity: "Warranty Claim",
                quickAddReactRoute: (request) => {
                    if (request.builderInfo?.flags?.warrantiesModalConversion) {
                        const to = warrantyAddRoute.link({ jobId: request.jobId });
                        return getQuickAddReactRoute(typeof to === "string" ? to : to.pathname);
                    }
                    return getQuickAddReactRoute(routes.warranty.getDetailsLink(0, request.jobId));
                },
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    timeClock: IMenuTabInfo = {
        testid: "timeClock",
        menuItemName: MenuItemName.TimeClock,
        icon: <BTIconTimeClock size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.timeClock.getListLink()),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Shift",
                quickAddReactRoute: (request) =>
                    getEditModeLink(
                        getQuickAddReactRoute(
                            routes.timeClock.getShiftDetailsLink(0, request.jobId),
                        ),
                    ),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    specifications: IMenuTabInfo = {
        testid: "specifications",
        menuItemName: MenuItemName.Specifications,
        icon: <BTIconSpecifications size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.plans.getPlansListLink());
        },
        quickAddInfo: [
            {
                entity: "Specification",
                quickAddReactRoute: () => {
                    return `${routes.specification.getBookLink()}?action=new`;
                },
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
        tag: <BTStatusBadge testid="specificationsNewTag" textOnly text="New" displayType="info" />,
    };
    clientUpdates: IMenuTabInfo = {
        testid: "clientUpdates",
        menuItemName: MenuItemName.ClientUpdates,
        icon: (
            // BTIcons have a width of 24px, while BDS icons have a width of 20px. This is a workaround to make them line up vertically.
            // This can be removed once all icons are BDS icons.
            <div className="flex justify-content-center" style={{ width: "24px" }}>
                <IconClientUpdates />
            </div>
        ),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.clientUpdates.getListLink()),
        quickAddInfo: [],
        showInTemplateMode: () => false,
        getTitle: getTemplateModeTitleDefault,
    };
    submittals: IMenuTabInfo = {
        testid: "submittals",
        menuItemName: MenuItemName.Submittals,
        icon: (
            <div className="flex justify-content-center" style={{ width: "24px" }}>
                <IconStamp />
            </div>
        ),
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.submittal.getListLink()),
        quickAddInfo: [],
        getTitle: getTemplateModeTitleDefault,
        tag: <BTStatusBadge testid="submittal-new-tag" textOnly text="New" displayType="info" />,
    };
}

class FilesMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "files-main-nav-button";
    documents: IMenuTabInfo = {
        testid: "documents",
        menuItemName: MenuItemName.Documents,
        icon: <BTIconDocuments size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.document.getListLink()),
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    photos: IMenuTabInfo = {
        testid: "photos",
        menuItemName: MenuItemName.Photos,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconPhotos size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.photo.getListLink()),
        showInTemplateMode: () => true,
    };
    videos: IMenuTabInfo = {
        testid: "videos",
        menuItemName: MenuItemName.Videos,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconVideos size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.video.getListLink()),
        showInTemplateMode: () => true,
    };
}

class MessagingMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "messaging-main-nav-button";
    comments: IMenuTabInfo = {
        testid: "comments",
        menuItemName: MenuItemName.Comments,
        icon: <BTIconComments size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.comment.getListLink()),
    };
    messages: IMenuTabInfo = {
        testid: "messages",
        menuItemName: MenuItemName.Messages,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconMessages size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.messages.getListLink()),
        quickAddInfo: [
            {
                entity: "Message",
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.messages.getComposeLink(0, request.jobId)),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    rfis: IMenuTabInfo = {
        testid: "rfis",
        menuItemName: MenuItemName.RFIs,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconRFIs size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.rfi.getListLink()),
        quickAddInfo: [
            {
                entity: "RFI",
                quickAddReactRoute: (request) =>
                    getEditModeLink(
                        getQuickAddReactRoute(routes.rfi.getDetailsLink(0, request.jobId)),
                    ),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    notificationHistory: IMenuTabInfo = {
        testid: "notificationHistory",
        menuItemName: MenuItemName.NotificationHistory,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconClockCounterClockwise size="large" />,
        getLinkUrl: () => getSPACheckedRoute(routes.notifications.getLegacyListLink()),
        getTitle: getTemplateModeTitleDefault,
    };
    surveys: IMenuTabInfo = {
        testid: "surveys",
        menuItemName: MenuItemName.Surveys,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconSurveys size="large" />,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            return getSPACheckedRoute(routes.survey.getListLink(info.loginType));
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
}

class FinancialMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "financial-main-nav-button";
    bids: IMenuTabInfo = {
        testid: "bids",
        menuItemName: MenuItemName.Bids,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconBidPackages size="large" />,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (info.loginType === BTLoginTypes.BUILDER) {
                return getSPACheckedRoute(routes.bidPackage.getListLink());
            } else if (info.loginType === BTLoginTypes.SUBS) {
                return getSPACheckedRoute(routes.bid.getListLink());
            }
            throw new Error("Invalid login type");
        },
        quickAddInfo: [
            {
                entity: "Bid Package",
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.bidPackage.getDetailsLink(0, request.jobId)),
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    estimates: IMenuTabInfo = {
        testid: "estimates",
        menuItemName: MenuItemName.Estimate,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconEstimate size="large" />,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            if (info.loginType === BTLoginTypes.OWNER || info.loginType === BTLoginTypes.CONTACT) {
                return getSPACheckedRoute(routes.estimate.getOwnerProposalMainNavLink());
            }

            return getSPACheckedRoute(routes.estimate.getListLink());
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    purchaseOrders: IMenuTabInfo = {
        testid: "purchaseOrders",
        menuItemName: MenuItemName.PurchaseOrders,
        icon: <BTIconPurchaseOrders size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.purchaseOrder.getListLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Purchase Order",
                useLabel: true,
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.purchaseOrder.getDetailsLink(0, request.jobId)),
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    bills: IMenuTabInfo = {
        testid: "bills",
        menuItemName: MenuItemName.Bills,
        icon: <BTIconBillsPurchaseOrders size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.bill.getListLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        quickAddInfo: [
            {
                entity: "Bill",
                useLabel: true,
                quickAddReactRoute: (request) => {
                    if (request.builderInfo?.flags.billsFullPageConversion) {
                        const to = billAddRoute.link({ jobId: request.jobId });
                        return getQuickAddReactRoute(typeof to === "string" ? to : to.pathname);
                    }
                    return getQuickAddReactRoute(routes.bill.getDetailsLink(0, request.jobId));
                },
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    // Schedule of Values (AiaPurchaseOrder feature). Builder portal only. Server-side visibility
    // (nav response `visible`, gated on the flag + PurchaseOrders permission) lands with the API layer.
    scheduleValues: IMenuTabInfo = {
        testid: "scheduleValues",
        menuItemName: MenuItemName.ScheduleValues,
        icon: <BTIconLeadProposals size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.scheduleOfValues.getListLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        showOnPortal: [BTLoginTypes.BUILDER],
        showInTemplateMode: () => false,
        getTitle: getTemplateModeTitleDefault,
    };
    budget: IMenuTabInfo = {
        testid: "budget",
        // Builders land on the legacy budget page (Budget); owners land on the owner budget page,
        // which reports BillsPurchaseOrders. Both are reached through this single nav item.
        menuItemName: [MenuItemName.Budget, MenuItemName.BillsPurchaseOrders],
        icon: <BTIconBudget size="large" />,
        onClickType: () => MenuOnClickTypes.BTLink,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            const isBuilder = info?.loginType === BTLoginTypes.BUILDER;

            if (isBuilder) {
                return `${routes.appBase}${routes.budget.getLegacyLink()}`;
            }

            return `${routes.appBase}${routes.ownerBudget.getListLink()}`;
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
        tag: <BTStatusBadge testid="legacyBudgetMenuTag" textOnly text="Legacy" />,
    };
    jobCostingBudget: IMenuTabInfo = {
        testid: "jobCostingBudget",
        menuItemName: MenuItemName.JobCostingBudget,
        icon: <BTIconBudget size="large" />,
        onClickType: () => MenuOnClickTypes.BTLink,
        getLinkUrl: () => `${routes.appBase}${routes.jobCostingBudget.getLink()}`,
        showInTemplateMode: () => true,
    };
    rebates: IMenuTabInfo = {
        testid: "rebates",
        menuItemName: MenuItemName.Rebates,
        icon: <BTIconRebates size="large" />,
        showOnPortal: [BTLoginTypes.BUILDER],
        getLinkUrl: () =>
            getSPACheckedRoute(
                routes.purchasing.getDashboardLink(PurchasingDashboardPages.Activity),
            ),
        getTitle: getTemplateModeTitleDefault,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        showInTemplateMode: () => false,
    };
    receipts: IMenuTabInfo = {
        testid: "receipts",
        menuItemName: MenuItemName.Receipts,
        icon: <BTIconReceipts size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.receipts.getListLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        showInTemplateMode: () => false,
        showOnPortal: [BTLoginTypes.BUILDER],
        quickAddInfo: [
            {
                entity: "Receipt",
                useLabel: true,
                quickAddReactRoute: () => getQuickAddReactRoute(routes.receipts.getUploadLink()),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    ownerInvoices: IMenuTabInfo = {
        testid: "ownerInvoices",
        menuItemName: [MenuItemName.OwnerPayments, MenuItemName.Deposits],
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconOwnerInvoices size="large" />,
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.ownerInvoice.getListLink());
        },
        quickAddInfo: [
            {
                entity: "Invoice",
                useLabel: true,
                quickAddReactRoute: (request) =>
                    getQuickAddReactRoute(routes.ownerInvoice.getDetailsLink(0, request.jobId)),
            },
        ],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    onlinePaymentSettings: IMenuTabInfo = {
        testid: "onlinePaymentSettings",
        menuItemName: MenuItemName.AcceptOnlinePayments,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconSettingsBlack size="large" />,
        showInTemplateMode: () => true,
        getLinkUrl: () => getSPACheckedRoute(routes.onlinePayments.paymentOverview),
        getTitle: getTemplateModeTitleDefault,
    };
    onlinePaymentReport: IMenuTabInfo = {
        testid: "onlinePaymentReport",
        menuItemName: MenuItemName.OnlinePaymentReport,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        icon: <BTIconTable size="large" />,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) =>
            getSPACheckedRoute(routes.onlinePayments.getPaymentReportLink(info.loginType)),
        getTitle: getTemplateModeTitleDefault,
    };
}

class ReportsMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "reports-main-nav-button";
    reports: IMenuTabInfo = {
        testid: "reports",
        menuItemName: MenuItemName.Reporting,
        showOnPortal: [BTLoginTypes.BUILDER],
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.reporting.reportList.getLink());
        },
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        showInTemplateMode: () => false,
        getTitle: getTemplateModeTitleDefault,
    };
}

class UsersMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "users-main-nav-button";
    internalUsers: IMenuTabInfo = {
        testid: "internalUsers",
        menuItemName: MenuItemName.Users,
        icon: <BTIconInternalUsers size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => {
            return `${routes.appBase}${routes.internalUsers.getListLink()}`;
        },
        quickAddInfo: [
            {
                entity: "Internal User",
                quickAddReactRoute: () =>
                    getEditModeLink(
                        getQuickAddReactRoute(routes.internalUsers.getInternalUserDetailsLink(0)),
                    ),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    subs: IMenuTabInfo = {
        testid: "subs",
        menuItemName: MenuItemName.Subs,
        icon: <BTIconSubs size="large" />,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => getSPACheckedRoute(routes.sub.getListLink()),
        quickAddInfo: [
            {
                entity: "Sub/Vendor",
                quickAddReactRoute: () =>
                    getEditModeLink(getQuickAddReactRoute(routes.sub.getDetailsLink(0))),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
    contacts: IMenuTabInfo = {
        testid: "contacts",
        menuItemName: MenuItemName.Contacts,
        icon: <BTIconContacts size="large" />,
        getLinkUrl: () => {
            return `${routes.appBase}${routes.contact.getListLink()}`;
        },
        quickAddInfo: [
            {
                entity: "Client Contact",
                quickAddReactRoute: () =>
                    getEditModeLink(getQuickAddReactRoute(routes.contact.getDetailsLink(0, false))),
            },
        ],
        getTitle: getTemplateModeTitleDefault,
    };
}

class NotificationsMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "notifications-main-nav-button";
    notifications: IMenuTabInfo = {
        testid: "notifications",
        showOnPortal: [
            BTLoginTypes.BUILDER,
            BTLoginTypes.SUBS,
            BTLoginTypes.OWNER,
            BTLoginTypes.CONTACT,
        ],
        getTitle: getTemplateModeTitleDefault,
    };
}

class HelpMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "help-main-nav-button";
    contactUs: IMenuTabInfo = {
        testid: "contactUs",
        icon: <BTIconPhoneOutline size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: () => routes.contact.getContactUsLink(),
        getTitle: getTemplateModeTitleDefault,
    };
    supportChat: IMenuTabInfo = {
        testid: "supportChat",
        icon: <BTIconLiveChat size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.Anchor,
        onClick: (request, data, routeProps, appDefaultInfo) => showChat(appDefaultInfo),
        getTitle: getTemplateModeTitleDefault,
    };
    community: IMenuTabInfo = {
        testid: "community",
        icon: <BTIconCommunities size="large" />,
        showInTemplateMode: () => true,
        onClickType: (builderInfo, userInfo) =>
            !userInfo?.isImpersonatingBuilder ? MenuOnClickTypes.External : MenuOnClickTypes.NoLink,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) =>
            getCommunityUrl(info.appDefaultInfo, info.userInfo?.isImpersonatingBuilder ?? false) ??
            "",
        getTitle: getTemplateModeTitleDefault,
        showOnPortal: [BTLoginTypes.BUILDER],
        onClick: (request, data, routeProps, appDefaultInfo, userInfo) => {
            if (userInfo?.isImpersonatingBuilder) {
                void message.warn("Impersonated sessions do not have access to Community");
            }
        },
    };
    buildertrendLearningAcademy: IMenuTabInfo = {
        testid: "buildertrendLearningAcademy",
        icon: <BTIconLearningAcademy size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.External,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) =>
            getLearningAcademyUrl(info.appDefaultInfo) ?? "",
        getTitle: getTemplateModeTitleDefault,
    };
    helpCenter: IMenuTabInfo = {
        testid: "helpCenter",
        icon: <BTIconHelpCenter size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.External,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) =>
            routes.help.getHelpCenterUrlForLoginType(info.loginType),
        getTitle: getTemplateModeTitleDefault,
    };
    giveFeedback: IMenuTabInfo = {
        testid: "giveFeedback",
        icon: <BTIconBulb size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.External,
        getLinkUrl: () => "",
        getTitle: getTemplateModeTitleDefault,
    };
    emailSupport: IMenuTabInfo = {
        testid: "emailSupport",
        icon: <BTIconMessages size="large" />,
        getLinkUrl: () => "mailto:support@buildertrend.com",
        showOnPortal: [BTLoginTypes.SUBS],
        getTitle: getTemplateModeTitleDefault,
    };
    legal: IMenuTabInfo = {
        testid: "legal",
        icon: <BTIconBank size="large" />,
        getLinkUrl: () => externalPages.BuildertrendLegal,
        onClickType: () => MenuOnClickTypes.External,
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
}

/**
 * A component rather than inline JSX because `MenuInfo` is instantiated at module scope, before any
 * provider mounts, so the context read has to be deferred to render time.
 */
const UserPreferencesIcon: React.FC = () => {
    const userInfo = useContext(UserInfoContext);

    if (!userInfo?.globalUserId) {
        return null;
    }

    return (
        <span className="ant-menu-item-icon">
            <BTUser
                size="small"
                displayName={userInfo.fullName ?? ""}
                globalUserId={userInfo.globalUserId}
                contactCard="none"
            />
        </span>
    );
};

class SetupMenu {
    [key: string]: IMenuTabInfo | string;
    testid = "settings-main-nav-button";
    userPreferences: IMenuTabInfo = {
        testid: "userPreferences",
        icon: <UserPreferencesIcon />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: (_info: IMenuTabInfoLinkUrlInfo) => {
            // Build previous-location state via the flat route (usePrevious defaults true),
            // but keep the query in a separate `search` field: history only splits `?query`
            // out of a STRING `to`, not an object descriptor, so query-in-pathname would
            // break route matching on push navigation (modal wouldn't open).
            const routeState = myUserDetailsRoute.link(undefined) as IRouteLocationState;
            return {
                ...routeState,
                search: `?initialTab=${InternalUserTabKeys.Overview}&upm=true`,
            };
        },
        getTitle: getTemplateModeTitleDefault,
    };
    settings: IMenuTabInfo = {
        testid: "settings",
        icon: <BTIconGearSix size={24} />,
        showInTemplateMode: () => true,
        onClickType: () => (isSinglePageApp ? MenuOnClickTypes.BTLink : MenuOnClickTypes.Anchor),
        getLinkUrl: () => {
            return getSPACheckedRoute(routes.settings.getSettingsLink());
        },
        getTitle: getTemplateModeTitleDefault,
    };
    subSettings: IMenuTabInfo = {
        testid: "subSettings",
        icon: <BTIconSettingsBlack size="large" />,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            return routes.sub.getSetupLink(info.userInfo!.globalUserId!);
        },
        showOnPortal: [BTLoginTypes.SUBS],
        getTitle: getTemplateModeTitleDefault,
    };
    ownerSettings: IMenuTabInfo = {
        testid: "ownerSettings",
        icon: <BTIconSettingsBlack size="large" />,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: (info: IMenuTabInfoLinkUrlInfo) => {
            return routes.owner.getSetupLink(info.jobId);
        },
        showOnPortal: [BTLoginTypes.OWNER, BTLoginTypes.CONTACT],
        getTitle: getTemplateModeTitleDefault,
    };
    manageSubscription: IMenuTabInfo = {
        testid: "manageSubscription",
        icon: <BTIconSubscription size="large" />,
        onClickType: () => MenuOnClickTypes.BTLinkRelative,
        getLinkUrl: () => {
            return routes.subscriptionManager.getSettingsLink(true, false);
        },
        showOnPortal: [BTLoginTypes.BUILDER],
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };
    syncSchedule: IMenuTabInfo = {
        testid: "syncSchedule",
        icon: <BTIconArrowsClockwise size="large" />,
        showInTemplateMode: () => true,
        onClickType: () => MenuOnClickTypes.NoLink,
        onClick: () => {
            // eslint-disable-next-line no-restricted-syntax
            (window as any).ReactDialogHandler.Settings.OpenQuickBooksSyncSchedule(refreshOnClose);
        },
        getTitle: getTemplateModeTitleDefault,
    };

    buildertrendMarketplace: IMenuTabInfo = {
        testid: "buildertrendMarketplace",
        onClickType: () => MenuOnClickTypes.External,
        icon: <BTIconMarketplace size="large" />,
        getLinkUrl: () => {
            return routes.marketplace.getMarketplaceLink();
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };

    integrations: IMenuTabInfo = {
        testid: "integrations",
        onClickType: () => MenuOnClickTypes.External,
        icon: <BTIconIntegrations size="large" />,
        getLinkUrl: () => {
            return routes.marketplace.getMarketplaceLink();
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };

    additionalServices: IMenuTabInfo = {
        testid: "additionalServices",
        onClickType: () => MenuOnClickTypes.External,
        icon: <BTIconAdditionalServices size="large" />,
        getLinkUrl: () => {
            return routes.additionalServices.getAdditionalServicesLink();
        },
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
    };

    logout: IMenuTabInfo = {
        testid: "logout",
        getIsDisabled: (userInfo?: UserInfo) => {
            return userInfo?.isBuilderImpersonatingOwner;
        },
        icon: <BTIconSignOut size="large" />,
        showInTemplateMode: () => true,
        getTitle: getTemplateModeTitleDefault,
        onClick: (request, data, routeProps, appDefaultInfo) => {
            handleLogout(appDefaultInfo);
        },
    };
}

/**
 * Menu Structure Classes
 */
export class MenuInfo {
    sales: SalesMenu = new SalesMenu();
    job: JobMenu = new JobMenu();
    projectManagement: ProjectManagementMenu = new ProjectManagementMenu();
    files: FilesMenu = new FilesMenu();
    messaging: MessagingMenu = new MessagingMenu();
    financial: FinancialMenu = new FinancialMenu();
    reports: ReportsMenu = new ReportsMenu();
    users: UsersMenu = new UsersMenu();
    help: HelpMenu = new HelpMenu();
    setup: SetupMenu = new SetupMenu();
    notifications: NotificationsMenu = new NotificationsMenu();
}

export class NavHeaderTabs {
    sales: NavHeaderResponse & {
        leadOpportunities?: NavMenuItemResponse;
        leadActivities?: NavMenuItemResponse;
        leadMap?: NavMenuItemResponse;
        leadProposals?: NavMenuItemResponse;
        activityCalendar?: NavMenuItemResponse;
        preliminaryEstimate?: NavMenuItemResponse;
    };

    job: NavHeaderResponse & {
        summary?: NavMenuItemResponse;
        jobInfo?: NavMenuItemResponse;
        jobPriceSummary?: NavMenuItemResponse;
        jobsList?: NavMenuItemResponse;
        jobsMap?: NavMenuItemResponse;
        newJobFromScratch?: NavMenuItemResponse;
        newJobFromTemplate?: NavMenuItemResponse;
    };

    projectManagement: NavHeaderResponse & {
        schedule?: NavMenuItemResponse;
        tasks?: NavMenuItemResponse;
        dailyLogs?: NavMenuItemResponse;
        todos?: NavMenuItemResponse;
        changeOrders?: NavMenuItemResponse;
        selections?: NavMenuItemResponse;
        options?: NavMenuItemResponse;
        warranty?: NavMenuItemResponse;
        timeClock?: NavMenuItemResponse;
        submittals?: NavMenuItemResponse;
    };

    files: NavHeaderResponse & {
        documents?: NavMenuItemResponse;
        photos?: NavMenuItemResponse;
        videos?: NavMenuItemResponse;
    };

    messaging: NavHeaderResponse & {
        comments?: NavMenuItemResponse;
        messages?: NavMenuItemResponse;
        rfis?: NavMenuItemResponse;
        notificationHistory?: NavMenuItemResponse;
        surveys?: NavMenuItemResponse;
    };

    financial: NavHeaderResponse & {
        bids?: NavMenuItemResponse;
        estimates?: NavMenuItemResponse;
        bills?: NavMenuItemResponse;
        purchaseOrders?: NavMenuItemResponse;
        scheduleValues?: NavMenuItemResponse;
        budget?: NavMenuItemResponse;
        jobCostingBudget?: NavMenuItemResponse;
        receipts?: NavMenuItemResponse;
        ownerInvoices?: NavMenuItemResponse;
        onlinePaymentReport?: NavMenuItemResponse;
    };

    reports: NavHeaderResponse & {
        reports?: NavMenuItemResponse;
    };

    users: NavHeaderResponse & {
        internalUsers?: NavMenuItemResponse;
        subs?: NavMenuItemResponse;
        contacts?: NavMenuItemResponse;
    };

    notifications: NavHeaderResponse & {
        notifications?: NavMenuItemResponse;
    };

    help: NavHeaderResponse & {
        contactUs?: NavMenuItemResponse;
        liveChat?: NavMenuItemResponse;
        helpCenter?: NavMenuItemResponse;
        giveFeedback?: NavMenuItemResponse;
    };

    setup: NavHeaderResponse & {
        userPreferences?: NavMenuItemResponse;
        settings?: NavMenuItemResponse;
        integrations?: NavMenuItemResponse;
        onlinePaymentSettings?: NavMenuItemResponse;
        additionalServices?: NavMenuItemResponse;
        logout?: NavMenuItemResponse;
    };
}
